// Copyright (c) Mysten Labs, Inc. — DGC: address page
// SPDX-License-Identifier: Apache-2.0

import { isSuiNSName, useResolveSuiNSAddress, useResolveSuiNSName } from '@mysten/core';
import { LoadingIndicator } from '@mysten/ui';
import { Link, useParams } from 'react-router-dom';

import { CpcAddressBalance } from '~/components/cpc/CpcAddressBalance';
import { PageLayout } from '~/components/Layout/PageLayout';
import { OwnedCoins } from '~/components/OwnedCoins';
import { OwnedObjects } from '~/components/OwnedObjects';
import { ErrorBoundary } from '~/components/error-boundary/ErrorBoundary';
import { TransactionsForAddress } from '~/components/transactions/TransactionsForAddress';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useGenesisPoolForAddress } from '~/hooks/useGenesisPoolForAddress';
import { Divider } from '~/ui/Divider';
import { LOCAL_STORAGE_SPLIT_PANE_KEYS, SplitPanes } from '~/ui/SplitPanes';
import { TabHeader, TabsList, TabsTrigger } from '~/ui/Tabs';
import { tryFormatCpcAddress, tryNormalizeToHexAddress } from '~/utils/cpcAddress';
import { TotalStaked } from './TotalStaked';

const LEFT_RIGHT_PANEL_MIN_SIZE = 30;
const TOP_PANEL_MIN_SIZE = 20;

function AddressResultPageHeader({ address, loading }: { address: string; loading?: boolean }) {
	const rpcAddress = tryNormalizeToHexAddress(address);
	const display = tryFormatCpcAddress(address);
	const { data: domainName, isLoading } = useResolveSuiNSName(rpcAddress);
	const { pool } = useGenesisPoolForAddress(rpcAddress);
	const busy = loading || isLoading;

	return (
		<div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
			<div className="min-w-0 space-y-2">
				<p className="text-xs font-medium uppercase tracking-wide text-[#94a3b8]">
					{pool ? '创世分配池地址' : '账户地址'}
				</p>
				{busy ? (
					<p className="text-sm text-[#94a3b8]">加载中…</p>
				) : (
					<>
						{pool ? (
							<p className="text-base font-semibold text-[#7dd3fc]">
								{pool.name}
								<span className="ml-2 text-xs font-normal text-[#94a3b8]">
									· {pool.vestingText}
								</span>
							</p>
						) : null}
						<h1 className="break-all font-mono text-lg font-semibold tracking-tight text-white sm:text-xl md:text-2xl">
							{display}
						</h1>
						{domainName ? <p className="text-sm text-[#38BDF8]">{domainName}</p> : null}
						<p className="break-all font-mono text-[11px] leading-relaxed text-[#64748b]">{rpcAddress}</p>
						{pool ? (
							<p className="max-w-xl text-xs leading-relaxed text-[#94a3b8]">
								这是创世「{pool.name}」资金池（计划额度 {pool.amount.toLocaleString('zh-CN')} DGC）。
								下方总余额是链上实际持币，可能因手续费/转出略少于计划额度。
								<Link to="/genesis" className="ml-1 text-[#38BDF8] hover:underline">
									分配表
								</Link>
							</p>
						) : null}
					</>
				)}
			</div>
			<div className="shrink-0">
				<TotalStaked address={rpcAddress} />
			</div>
		</div>
	);
}

function SuiNSAddressResultPageHeader({ name }: { name: string }) {
	const { data: address, isLoading } = useResolveSuiNSAddress(name);

	return <AddressResultPageHeader address={address ?? name} loading={isLoading} />;
}

function AddressResult({ address }: { address: string }) {
	const isMediumOrAbove = useBreakpoint('md');
	const rpcAddress = tryNormalizeToHexAddress(address);

	const leftPane = {
		panel: <OwnedCoins id={rpcAddress} />,
		minSize: LEFT_RIGHT_PANEL_MIN_SIZE,
		defaultSize: LEFT_RIGHT_PANEL_MIN_SIZE,
	};

	const rightPane = {
		panel: <OwnedObjects id={rpcAddress} />,
		minSize: LEFT_RIGHT_PANEL_MIN_SIZE,
	};

	const topPane = {
		panel: (
			<div className="flex flex-col gap-3">
				<CpcAddressBalance address={rpcAddress} />
				<ErrorBoundary>
					{isMediumOrAbove ? (
						<div className="min-h-[280px] rounded-lg bg-[#0F172A] p-4">
							<SplitPanes
								autoSaveId={LOCAL_STORAGE_SPLIT_PANE_KEYS.ADDRESS_VIEW_HORIZONTAL}
								dividerSize="none"
								splitPanels={[leftPane, rightPane]}
								direction="horizontal"
							/>
						</div>
					) : (
						<>
							{leftPane.panel}
							<div className="my-8">
								<Divider />
							</div>
							{rightPane.panel}
						</>
					)}
				</ErrorBoundary>
			</div>
		),
		minSize: TOP_PANEL_MIN_SIZE,
	};

	const bottomPane = {
		panel: (
			<div className="flex min-h-[240px] flex-col rounded-xl bg-[#0F172A] pb-4">
				<div className="rounded-t-xl bg-white/10 px-6 py-4">
					<TabsList>
						<TabsTrigger value="tab" className="data-[state=active]:text-[#38BDF8]">
							交易记录
						</TabsTrigger>
					</TabsList>
				</div>

				<ErrorBoundary>
					<div data-testid="tx" className="relative mt-4 min-h-14 overflow-auto p-4">
						<TransactionsForAddress address={rpcAddress} type="address" />
					</div>
				</ErrorBoundary>
			</div>
		),
	};

	return (
		<TabHeader title="持有资产" noGap>
			{/* Stack panels instead of fixed-height vertical SplitPanes (h-300 clipped tx history). */}
			<div className="flex flex-col gap-5">
				{topPane.panel}
				{bottomPane.panel}
			</div>
		</TabHeader>
	);
}

function SuiNSAddressResult({ name }: { name: string }) {
	const { isFetched, data } = useResolveSuiNSAddress(name);

	if (!isFetched) {
		return <LoadingIndicator />;
	}

	return <AddressResult address={data ?? name} />;
}

export default function AddressResultPage() {
	const { id } = useParams();
	const isSuiNSAddress = isSuiNSName(id!);

	return (
		<PageLayout
			gradient={{
				size: 'md',
				content: isSuiNSAddress ? (
					<SuiNSAddressResultPageHeader name={id!} />
				) : (
					<AddressResultPageHeader address={id!} />
				),
			}}
			content={isSuiNSAddress ? <SuiNSAddressResult name={id!} /> : <AddressResult address={id!} />}
		/>
	);
}
